import ArticleShell from "@/components/site/ArticleShell";

const BodyEs = () => (
  <div className="article-body">
    <p>En sesión, cuando le pregunto a una mujer que está atravesando un cáncer cómo se habla a sí misma cuando está cansada o asustada, casi todas responden algo parecido:</p>
    <ul>
      <li><em>"Tengo que ser fuerte."</em></li>
      <li><em>"No puedo permitirme estar así."</em></li>
      <li><em>"Debería estar mejor a estas alturas."</em></li>
      <li><em>"Si no me animo yo, ¿quién?"</em></li>
    </ul>
    <p>Y entonces les hago la pregunta clave: <strong>"¿Le hablarías así a una amiga que estuviera atravesando lo mismo que tú?"</strong></p>
    <p>Casi siempre se quedan en silencio. Porque la respuesta es no.</p>
    <p>Aquí, justo en ese silencio, empieza la autocompasión.</p>
    <h2>Qué es la autocompasión (y qué no es)</h2>
    <p>El concepto viene del trabajo de la <strong>Dra. Kristin Neff</strong> y <strong>Christopher Germer</strong>. La autocompasión tiene tres componentes:</p>
    <ol>
      <li><strong>Amabilidad hacia una misma</strong> en lugar de juicio severo.</li>
      <li><strong>Sentido de humanidad común</strong>: reconocer que el sufrimiento es parte de la experiencia humana.</li>
      <li><strong>Mindfulness</strong>: estar con lo que sentimos sin exagerarlo ni reprimirlo.</li>
    </ol>
    <p>La autocompasión <strong>NO es</strong>: lástima por una misma, dejadez, excusa para no hacer lo que toca, pensar que eres especial.</p>
    <p>Es, simplemente, <strong>tratarte como tratarías a alguien a quien quieres mucho</strong>.</p>
    <blockquote>Si una amiga estuviera viviendo lo que tú estás viviendo, ¿qué le dirías? Pues háblate a ti igual.</blockquote>
    <h2>Por qué importa especialmente cuando hay cáncer</h2>
    <p>Un diagnóstico de cáncer es un trauma: amenaza vital, pérdida de control, ruptura de la sensación de seguridad. Ante un trauma activamos un patrón concreto: <strong>nos volvemos extremadamente exigentes con nosotras mismas</strong>.</p>
    <p>Esa autoexigencia, en oncología, hace daño concreto:</p>
    <ul>
      <li><strong>Aumenta el cortisol</strong>, dificultando sueño y recuperación física.</li>
      <li><strong>Mantiene el sistema nervioso en alerta</strong>, alimentando fatiga y dolor.</li>
      <li><strong>Impide pedir ayuda</strong>, porque pedir se siente como fallar.</li>
      <li><strong>Bloquea las emociones</strong> hasta que estallan en cuerpo o relaciones.</li>
    </ul>
    <h2>Lo que dice la investigación</h2>
    <p>Las intervenciones basadas en autocompasión muestran:</p>
    <ul>
      <li>Reducción de <strong>ansiedad y depresión</strong> en supervivientes de cáncer.</li>
      <li>Mejor <strong>regulación emocional</strong>.</li>
      <li>Reducción del <strong>miedo a la recidiva</strong>.</li>
      <li>Mejor <strong>calidad de vida</strong>.</li>
    </ul>
    <p>El programa más estudiado se llama <strong>MSC (Mindful Self-Compassion)</strong>.</p>
    <h2>Cómo se practica la autocompasión, en concreto</h2>
    <h3>1. La pausa autocompasiva</h3>
    <ol>
      <li><strong>Reconocer:</strong> "Esto es un momento de dolor / dificultad."</li>
      <li><strong>Humanidad común:</strong> "Otras personas se sienten así también."</li>
      <li><strong>Amabilidad:</strong> "¿Qué necesito ahora?"</li>
    </ol>
    <p>Tres minutos. Cambia el día.</p>
    <h3>2. Mano en el corazón</h3>
    <p>Pon una mano sobre el pecho. El contacto físico cálido activa el sistema de cuidado y libera oxitocina. <strong>Fisiología del cuidado</strong>. Quédate ahí 5 respiraciones largas.</p>
    <h3>3. La pregunta de la amiga</h3>
    <p>Cuando te oigas hablándote duro, para y pregúntate: <strong>¿qué le diría a una amiga querida?</strong> Pues háblate igual.</p>
    <h3>4. Una frase mantra</h3>
    <ul>
      <li><em>"Que pueda ser amable conmigo en este momento."</em></li>
      <li><em>"Estoy haciendo lo que puedo."</em></li>
      <li><em>"Que pueda darme lo que necesito."</em></li>
      <li><em>"Esto también pasará."</em></li>
    </ul>
    <h2>El miedo más común a practicar autocompasión</h2>
    <p>Muchas mujeres me dicen: <em>"Si soy amable conmigo, me voy a relajar y no voy a hacer lo que toca."</em> La investigación dice lo contrario. Las personas más autocompasivas:</p>
    <ul>
      <li><strong>Cumplen mejor</strong> los tratamientos médicos.</li>
      <li><strong>Cuidan mejor</strong> sueño, alimentación y cuerpo.</li>
      <li><strong>Piden ayuda antes</strong>.</li>
      <li><strong>Se recuperan mejor</strong> de los días difíciles.</li>
    </ul>
    <p>La autoexigencia agota. La autocompasión sostiene.</p>
    <blockquote>La autocompasión no debilita. Sostiene. Y lo sostenido es lo único que aguanta procesos largos.</blockquote>
    <h2>Si esto te ha resonado</h2>
    <p>En Método TAO la autocompasión es la base sobre la que se construye cada sesión. Trabajamos cuerpo y mente desde el permiso, no desde la exigencia.</p>
    <p>Te invito a una <strong>llamada gratuita de 15 minutos</strong>. Sin guion. Sin compromiso.</p>
    <p>Y mientras tanto, hoy, hazte una pregunta antes de dormir: <em>¿qué necesita la persona que soy hoy?</em> Y dáselo.</p>
  </div>
);

const BodyCa = () => (
  <div className="article-body">
    <p>A sessió, quan pregunto a una dona que travessa un càncer com es parla a si mateixa quan està cansada o espantada, gairebé totes responen una cosa semblant:</p>
    <ul>
      <li><em>"He de ser forta."</em></li>
      <li><em>"No em puc permetre estar així."</em></li>
      <li><em>"Hauria d'estar millor a aquestes alçades."</em></li>
      <li><em>"Si no m'animo jo, qui?"</em></li>
    </ul>
    <p>I aleshores els faig la pregunta clau: <strong>"Li parlaries així a una amiga que estigués travessant el mateix que tu?"</strong></p>
    <p>Gairebé sempre es queden en silenci. Perquè la resposta és no.</p>
    <p>Aquí, just en aquest silenci, comença l'autocompassió.</p>
    <h2>Què és l'autocompassió (i què no és)</h2>
    <p>El concepte ve del treball de la <strong>Dra. Kristin Neff</strong> i <strong>Christopher Germer</strong>. L'autocompassió té tres components:</p>
    <ol>
      <li><strong>Amabilitat cap a una mateixa</strong> en lloc de judici sever.</li>
      <li><strong>Sentit d'humanitat comuna</strong>: reconèixer que el patiment forma part de l'experiència humana.</li>
      <li><strong>Mindfulness</strong>: estar amb el que sentim sense exagerar-ho ni reprimir-ho.</li>
    </ol>
    <p>L'autocompassió <strong>NO és</strong>: llàstima per una mateixa, deixadesa, excusa per no fer el que toca, pensar que ets especial.</p>
    <p>És, simplement, <strong>tractar-te com tractaries algú que estimes molt</strong>.</p>
    <blockquote>Si una amiga estigués vivint el que tu estàs vivint, què li diries? Doncs parla't a tu igual.</blockquote>
    <h2>Per què importa especialment quan hi ha càncer</h2>
    <p>Un diagnòstic de càncer és un trauma: amenaça vital, pèrdua de control, ruptura de la sensació de seguretat. Davant un trauma activem un patró concret: <strong>ens tornem extremadament exigents amb nosaltres mateixes</strong>.</p>
    <p>Aquesta autoexigència, en oncologia, fa mal concret:</p>
    <ul>
      <li><strong>Augmenta el cortisol</strong>, dificultant son i recuperació física.</li>
      <li><strong>Manté el sistema nerviós en alerta</strong>, alimentant fatiga i dolor.</li>
      <li><strong>Impedeix demanar ajuda</strong>, perquè demanar se sent com fallar.</li>
      <li><strong>Bloqueja les emocions</strong> fins que esclaten al cos o a les relacions.</li>
    </ul>
    <h2>El que diu la investigació</h2>
    <p>Les intervencions basades en autocompassió mostren:</p>
    <ul>
      <li>Reducció d'<strong>ansietat i depressió</strong> en supervivents de càncer.</li>
      <li>Millor <strong>regulació emocional</strong>.</li>
      <li>Reducció de la <strong>por a la recidiva</strong>.</li>
      <li>Millor <strong>qualitat de vida</strong>.</li>
    </ul>
    <p>El programa més estudiat es diu <strong>MSC (Mindful Self-Compassion)</strong>.</p>
    <h2>Com es practica l'autocompassió, en concret</h2>
    <h3>1. La pausa autocompassiva</h3>
    <ol>
      <li><strong>Reconèixer:</strong> "Això és un moment de dolor / dificultat."</li>
      <li><strong>Humanitat comuna:</strong> "Altres persones se senten així també."</li>
      <li><strong>Amabilitat:</strong> "Què necessito ara?"</li>
    </ol>
    <p>Tres minuts. Canvia el dia.</p>
    <h3>2. Mà al cor</h3>
    <p>Posa una mà sobre el pit. El contacte físic càlid activa el sistema de cura i allibera oxitocina. <strong>Fisiologia de la cura</strong>. Queda't allí 5 respiracions llargues.</p>
    <h3>3. La pregunta de l'amiga</h3>
    <p>Quan et sentis parlant-te dur, para i pregunta't: <strong>què li diria a una amiga estimada?</strong> Doncs parla't igual.</p>
    <h3>4. Una frase mantra</h3>
    <ul>
      <li><em>"Que pugui ser amable amb mi en aquest moment."</em></li>
      <li><em>"Estic fent el que puc."</em></li>
      <li><em>"Que pugui donar-me el que necessito."</em></li>
      <li><em>"Això també passarà."</em></li>
    </ul>
    <h2>La por més comuna a practicar autocompassió</h2>
    <p>Moltes dones em diuen: <em>"Si sóc amable amb mi, em relaxaré i no faré el que toca."</em> La investigació diu el contrari. Les persones més autocompassives:</p>
    <ul>
      <li><strong>Compleixen millor</strong> els tractaments mèdics.</li>
      <li><strong>Cuiden millor</strong> son, alimentació i cos.</li>
      <li><strong>Demanen ajuda abans</strong>.</li>
      <li><strong>Es recuperen millor</strong> dels dies difícils.</li>
    </ul>
    <p>L'autoexigència esgota. L'autocompassió sosté.</p>
    <blockquote>L'autocompassió no debilita. Sosté. I el que es sosté és l'únic que aguanta processos llargs.</blockquote>
    <h2>Si això t'ha ressonat</h2>
    <p>Al Mètode TAO l'autocompassió és la base sobre la qual es construeix cada sessió. Treballem cos i ment des del permís, no des de l'exigència.</p>
    <p>Et convido a una <strong>trucada gratuïta de 15 minuts</strong>. Sense guió. Sense compromís.</p>
    <p>I mentrestant, avui, fes-te una pregunta abans de dormir: <em>què necessita la persona que sóc avui?</em> I dóna-li-ho.</p>
  </div>
);

const ArticleAutocompasion = () => (
  <ArticleShell
    bodyEs={<BodyEs />}
    bodyCa={<BodyCa />}
    leadMagnet={{
      guide: "post-cancer",
      source: "blog/autocompasion-cancer",
      title: { es: "Volver a habitar el cuerpo después del cáncer", ca: "Tornar a habitar el cos després del càncer" },
      subtitle: { es: "Si te ha resonado este artículo, te dejo una mini-guía con 5 prácticas suaves para volver al cuerpo desde la amabilidad, no desde la exigencia.", ca: "Si t'ha ressonat aquest article, et deixo una mini-guia amb 5 pràctiques suaus per tornar al cos des de l'amabilitat, no des de l'exigència." },
      bullets: {
        es: ["5 prácticas cuerpo-mente desde la autocompasión", "Pensadas para días buenos y días malos", "Sin esfuerzo físico — todo desde la escucha", "La recibes en tu email en menos de 5 minutos"],
        ca: ["5 pràctiques cos-ment des de l'autocompassió", "Pensades per a dies bons i dies dolents", "Sense esforç físic — tot des de l'escolta", "La reps al teu email en menys de 5 minuts"],
      },
    }}
  />
);

export default ArticleAutocompasion;
